import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, FileText, Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { fmtD, fmtDT, money } from "@/lib/semanas";
import { usePublicaciones, PubSelect, EmptyPubs } from "@/components/control/cneShared";

export function CnePrecios() {
  const { data: pubs, isLoading } = usePublicaciones();
  const [sel, setSel] = useState(null);
  const [estado, setEstado] = useState("all");
  const [q, setQ] = useState("");
  const pubId = sel || pubs?.[0]?.id;
  const pub = pubs?.find((p) => p.id === pubId);
  const { data: rows, isLoading: lr } = useQuery({
    queryKey: ["cne", "precios", pubId],
    queryFn: async () => (await api.get(`/control-semanal/cne/publicaciones/${pubId}/precios`)).data.items,
    enabled: !!pubId,
  });
  const estados = useMemo(() => [...new Set((rows || []).map((r) => r.estado))].sort(), [rows]);
  const shown = useMemo(() => (rows || []).filter((r) =>
    (estado === "all" || r.estado === estado) &&
    (!q || `${r.region} ${r.municipio} ${r.estado}`.toLowerCase().includes(q.toLowerCase()))), [rows, estado, q]);

  if (isLoading) return <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>;
  if (!pubs?.length) return <EmptyPubs />;
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 flex flex-wrap items-center gap-3">
        <PubSelect pubs={pubs} value={pubId} onChange={setSel} testid="cne-pub-select" />
        <Select value={estado} onValueChange={setEstado}>
          <SelectTrigger className="h-9 w-56 bg-slate-50 border-slate-200 text-sm" data-testid="cne-estado-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Todas las entidades</SelectItem>
            {estados.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar región o municipio" data-testid="cne-search"
            className="h-9 w-64 pl-9 pr-3 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none" />
        </div>
      </div>
      {pub && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500 bg-white rounded-2xl border border-slate-200/70 shadow-sm px-4 py-3" data-testid="cne-pub-info">
          <span className="font-semibold text-slate-800 inline-flex items-center gap-1.5"><FileText className="w-4 h-4 text-red-500" />{pub.titulo}</span>
          <span>Publicación: <b>{fmtD(pub.fecha_publicacion)}</b></span>
          <span>Vigencia: <b>{fmtD(pub.vigencia_inicio)} – {fmtD(pub.vigencia_fin)}</b></span>
          <span>{pub.regiones} regiones · {pub.registros} municipios</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Cargada {fmtDT(pub.imported_at)} por {pub.imported_by_name || "—"}</span>
          {pub.source_url && <a href={pub.source_url} target="_blank" rel="noreferrer" className="text-red-600 font-semibold hover:underline" data-testid="cne-source-link">Fuente oficial</a>}
        </div>
      )}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden">
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full text-sm" data-testid="cne-precios-table">
            <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-4 py-2.5">Región</th><th className="text-left px-4 py-2.5">Entidad federativa</th><th className="text-left px-4 py-2.5">Municipio</th><th className="text-right px-4 py-2.5">$/kg con IVA</th><th className="text-right px-4 py-2.5">$/litro con IVA</th></tr>
            </thead>
            <tbody>
              {lr ? <tr><td colSpan={5} className="py-10 text-center"><Loader2 className="w-5 h-5 text-red-600 animate-spin inline" /></td></tr> :
                shown.map((r, i) => (
                  <tr key={`${r.region}-${r.estado}-${r.municipio}-${i}`} className="border-t border-slate-100 hover:bg-red-50/30">
                    <td className="px-4 py-2 font-semibold text-slate-700">{r.region}</td>
                    <td className="px-4 py-2 text-slate-600">{r.estado}</td>
                    <td className="px-4 py-2 text-slate-800">{r.municipio}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(r.precio_kg)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(r.precio_lt)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100" data-testid="cne-count">{shown.length} registros</div>
      </div>
    </div>
  );
}
